import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Circle as CircleIcon, Gift, Check, X } from 'lucide-react';
import LanguageSelector from '../components/LanguageSelector';
import api from '../services/api';

const formatApiErrorDetail = (detail) => {
  if (detail == null) return null;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
};

const RegisterPage = () => {
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    city: '',
    level: 'beginner',
  });
  const [loading, setLoading] = useState(false);
  const [appleLoading, setAppleLoading] = useState(false);
  const [referralCode, setReferralCode] = useState('');
  const [referralValid, setReferralValid] = useState(null);
  const [referrerName, setReferrerName] = useState('');
  const [checkingReferral, setCheckingReferral] = useState(false);
  const { register, loginWithGoogle, loginWithApple } = useAuth();
  const navigate = useNavigate();
  const { t, language } = useTranslation();
  const { isDark } = useTheme();

  // Check for referral code in URL
  useEffect(() => {
    const refCode = searchParams.get('ref');
    if (refCode) {
      setReferralCode(refCode.toUpperCase());
      checkReferralCode(refCode.toUpperCase());
    }
  }, [searchParams]);

  const checkReferralCode = async (code) => {
    if (!code || code.length < 6) {
      setReferralValid(null);
      setReferrerName('');
      return;
    }
    
    setCheckingReferral(true);
    try {
      const { data } = await api.get(`/referral/check-code/${code}`);
      setReferralValid(data.valid);
      setReferrerName(data.referrerName || '');
    } catch (error) {
      setReferralValid(false);
      setReferrerName('');
    }
    setCheckingReferral(false);
  };

  // Debounce referral code check
  useEffect(() => {
    const timer = setTimeout(() => {
      if (referralCode.length >= 6) {
        checkReferralCode(referralCode);
      } else {
        setReferralValid(null);
        setReferrerName('');
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [referralCode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await register(formData);
      
      // Apply referral code after successful registration
      if (referralCode && referralValid) {
        try {
          await api.post(`/referral/apply`, 
            { referralCode: referralCode },
            { withCredentials: true }
          );
          toast.success(language === 'fr' 
            ? `Code de ${referrerName} appliqué ! 1 mois Premium après votre premier match.` 
            : `${referrerName}'s code applied! 1 month Premium after your first match.`
          );
        } catch (refError) {
          console.error('Referral apply error:', refError);
          // Don't block registration if referral fails
        }
      }
      
      toast.success(t('auth.registerSuccess'));
      navigate('/');
    } catch (error) {
      const errorMsg = formatApiErrorDetail(error.response?.data?.detail) || t('common.error');
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogleRegister = () => {
    loginWithGoogle();
  };

  const handleAppleRegister = async () => {
    setAppleLoading(true);
    try {
      const result = await loginWithApple();
      if (result && result.success) {
        if (result.needsProfileCompletion) {
          toast.info(t('auth.completeProfile') || 'Veuillez compléter votre profil');
          navigate('/profile/edit');
        } else {
          toast.success(t('auth.registerSuccess'));
          navigate('/');
        }
      }
    } catch (error) {
      // Handle user cancellation gracefully
      if (error.message === 'popup_closed_by_user' || error.error === 'popup_closed_by_user') {
        return;
      }
      const errorMsg = error.message || t('common.error');
      toast.error(errorMsg);
    } finally {
      setAppleLoading(false);
    }
  };

  return (
    <div className={`min-h-screen relative overflow-hidden ${isDark ? 'bg-[#0d1829]' : 'bg-gradient-to-br from-slate-50 to-slate-100'}`}>
      {/* Language Selector */}
      <div className="absolute top-4 right-4 z-20">
        <LanguageSelector />
      </div>

      {/* Background gradient overlay */}
      <div className={`absolute inset-0 z-0 ${isDark ? 'bg-gradient-to-br from-[#0d1829] via-[#0a1420] to-[#0d1829]' : 'bg-gradient-to-br from-white via-slate-50 to-blue-50'}`}>
        <div className={`absolute inset-0 ${isDark ? 'bg-[radial-gradient(ellipse_at_top_right,rgba(0,212,255,0.1),transparent_50%)]' : 'bg-[radial-gradient(ellipse_at_top_right,rgba(0,180,255,0.15),transparent_50%)]'}`}></div>
        <div className={`absolute inset-0 ${isDark ? 'bg-[radial-gradient(ellipse_at_bottom_left,rgba(200,230,0,0.05),transparent_50%)]' : 'bg-[radial-gradient(ellipse_at_bottom_left,rgba(0,150,200,0.08),transparent_50%)]'}`}></div>
      </div>

      <div className="relative z-10 min-h-screen flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="flex flex-col items-center gap-4 mb-4">
              <img 
                src="/icons/icon-192x192.png" 
                alt="XO Club" 
                className={`w-24 h-24 ${isDark ? 'neon-glow' : 'drop-shadow-lg'}`}
              />
            </div>
            <p className={`text-sm ${isDark ? 'text-[#5ce1e6]' : 'text-cyan-600 font-medium'}`}>{t('auth.joinCommunity')}</p>
          </div>

          <div className={`backdrop-blur-sm rounded-2xl p-8 ${isDark ? 'bg-[#0f1c2e]/90 border border-[#1e3a5f] neon-border' : 'bg-white/90 border border-slate-200 shadow-xl'}`}>
            <h2 className={`text-2xl font-bold mb-6 ${isDark ? 'text-white' : 'text-slate-800'}`}>{t('auth.register')}</h2>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="name" className={isDark ? 'text-zinc-300' : 'text-slate-700 font-medium'}>
                  {t('auth.name')}
                </Label>
                <Input
                  id="name"
                  data-testid="register-name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className={`transition-colors ${isDark ? 'bg-[#0a1420] border-[#1e3a5f] text-white focus:border-[#00d4ff] focus:ring-1 focus:ring-[#00d4ff]' : 'bg-white border-slate-300 text-slate-900 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500'}`}
                />
              </div>

              <div>
                <Label htmlFor="email" className={isDark ? 'text-zinc-300' : 'text-slate-700 font-medium'}>
                  {t('auth.email')}
                </Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="register-email-input"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className={`transition-colors ${isDark ? 'bg-[#0a1420] border-[#1e3a5f] text-white focus:border-[#00d4ff] focus:ring-1 focus:ring-[#00d4ff]' : 'bg-white border-slate-300 text-slate-900 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500'}`}
                />
              </div>

              <div>
                <Label htmlFor="password" className={isDark ? 'text-zinc-300' : 'text-slate-700 font-medium'}>
                  {t('auth.password')}
                </Label>
                <Input
                  id="password"
                  type="password"
                  data-testid="register-password-input"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  minLength={8}
                  className={`transition-colors ${isDark ? 'bg-[#0a1420] border-[#1e3a5f] text-white focus:border-[#00d4ff] focus:ring-1 focus:ring-[#00d4ff]' : 'bg-white border-slate-300 text-slate-900 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500'}`}
                />
              </div>

              <div>
                <Label htmlFor="city" className={isDark ? 'text-zinc-300' : 'text-slate-700 font-medium'}>
                  {t('auth.city')}
                </Label>
                <Input
                  id="city"
                  data-testid="register-city-input"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  className={`transition-colors ${isDark ? 'bg-[#0a1420] border-[#1e3a5f] text-white focus:border-[#00d4ff] focus:ring-1 focus:ring-[#00d4ff]' : 'bg-white border-slate-300 text-slate-900 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500'}`}
                />
              </div>

              <div>
                <Label htmlFor="level" className={isDark ? 'text-zinc-300' : 'text-slate-700 font-medium'}>
                  {t('auth.level')}
                </Label>
                <Select
                  value={formData.level}
                  onValueChange={(value) => setFormData({ ...formData, level: value })}
                >
                  <SelectTrigger
                    data-testid="register-level-select"
                    className={`transition-colors ${isDark ? 'bg-[#0a1420] border-[#1e3a5f] text-white focus:border-[#00d4ff] focus:ring-1 focus:ring-[#00d4ff]' : 'bg-white border-slate-300 text-slate-900 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500'}`}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className={isDark ? 'bg-[#0a1420] border-[#1e3a5f]' : 'bg-white border-slate-300'}>
                    <SelectItem value="beginner">{t('levels.beginner')}</SelectItem>
                    <SelectItem value="intermediate">{t('levels.intermediate')}</SelectItem>
                    <SelectItem value="advanced">{t('levels.advanced')}</SelectItem>
                    <SelectItem value="pro">{t('levels.pro')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Referral Code */}
              <div>
                <Label htmlFor="referralCode" className={`flex items-center gap-2 ${isDark ? 'text-zinc-300' : 'text-slate-700 font-medium'}`}>
                  <Gift size={14} className="text-purple-500" />
                  {language === 'fr' ? 'Code parrain (optionnel)' : 'Referral code (optional)'}
                </Label>
                <div className="relative">
                  <Input
                    id="referralCode"
                    data-testid="register-referral-input"
                    placeholder={language === 'fr' ? 'Ex: ABC123XY' : 'Ex: ABC123XY'}
                    value={referralCode}
                    onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                    maxLength={10}
                    className={`uppercase tracking-widest transition-colors ${
                      referralValid === true 
                        ? 'border-green-500 focus:border-green-500 focus:ring-green-500' 
                        : referralValid === false
                          ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                          : ''
                    } ${isDark ? 'bg-[#0a1420] border-[#1e3a5f] text-white focus:border-[#00d4ff] focus:ring-1 focus:ring-[#00d4ff]' : 'bg-white border-slate-300 text-slate-900 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500'}`}
                  />
                  {referralCode.length >= 6 && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      {checkingReferral ? (
                        <div className="w-4 h-4 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
                      ) : referralValid ? (
                        <Check size={18} className="text-green-500" />
                      ) : referralValid === false ? (
                        <X size={18} className="text-red-500" />
                      ) : null}
                    </div>
                  )}
                </div>
                {referralValid && referrerName && (
                  <p className="text-xs text-green-500 mt-1 flex items-center gap-1">
                    <Gift size={12} />
                    {language === 'fr' 
                      ? `Code de ${referrerName} ! Vous gagnerez 1 mois Premium.`
                      : `${referrerName}'s code! You'll earn 1 month Premium.`}
                  </p>
                )}
                {referralValid === false && referralCode.length >= 6 && (
                  <p className="text-xs text-red-500 mt-1">
                    {language === 'fr' ? 'Code invalide' : 'Invalid code'}
                  </p>
                )}
              </div>

              {/* Social Login Buttons */}
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className={`w-full border-t ${isDark ? 'border-[#1e3a5f]' : 'border-slate-300'}`}></div>
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className={`px-2 ${isDark ? 'bg-[#0f1c2e] text-zinc-500' : 'bg-white text-slate-500'}`}>{t('common.or')}</span>
                </div>
              </div>

              <Button
                type="button"
                variant="outline"
                data-testid="google-register-btn"
                onClick={handleGoogleRegister}
                className={`w-full flex items-center justify-center gap-2 transition-all ${isDark ? 'border-[#1e3a5f] text-white hover:bg-[#1e3a5f]/50 hover:border-[#00d4ff]' : 'border-slate-300 text-slate-700 bg-white hover:bg-slate-50 hover:border-cyan-500'}`}
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                {t('auth.continueWithGoogle')}
              </Button>

              <Button
                type="button"
                variant="outline"
                data-testid="apple-register-btn"
                onClick={handleAppleRegister}
                disabled={appleLoading}
                className={`w-full flex items-center justify-center gap-2 transition-all ${isDark ? 'border-[#1e3a5f] text-white hover:bg-[#1e3a5f]/50 hover:border-[#00d4ff]' : 'border-slate-300 text-slate-700 bg-white hover:bg-slate-50 hover:border-slate-400'}`}
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                </svg>
                {appleLoading ? 'Inscription...' : t('auth.continueWithApple')}
              </Button>

              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className={`w-full border-t ${isDark ? 'border-[#1e3a5f]' : 'border-slate-300'}`}></div>
                </div>
              </div>

              <Button
                type="submit"
                data-testid="register-submit-button"
                disabled={loading}
                className="w-full bg-gradient-to-r from-[#00d4ff] to-[#00b8d4] text-black font-bold hover:from-[#00b8d4] hover:to-[#0099b8] transition-all duration-200 shadow-[0_0_20px_rgba(0,212,255,0.3)]"
              >
                {loading ? t('auth.registerProgress') : t('auth.registerBtn')}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className={`text-sm ${isDark ? 'text-zinc-400' : 'text-slate-600'}`}>
                {t('auth.hasAccount')}{' '}
                <Link
                  to="/login"
                  data-testid="register-login-link"
                  className={`hover:underline font-medium ${isDark ? 'text-cyan-400' : 'text-cyan-600'}`}
                >
                  {t('auth.signIn')}
                </Link>
              </p>
              <p className={`text-xs mt-3 ${isDark ? 'text-zinc-500' : 'text-slate-500'}`}>
                {t('auth.isClub')}{' '}
                <Link
                  to="/club/register"
                  className={`hover:underline font-medium ${isDark ? 'text-[#c8e600]' : 'text-lime-600'}`}
                >
                  {t('auth.clubRegister')}
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
